import * as React from 'react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  usePageNotifications,
  useLogger,
  useAuth,
  useCustomers,
  useCustomerApi,
} from 'hooks'
import { AsyncPayload } from '../base'
import { Typography, Box, Accordion, Grid, Loader } from 'cs-kevlar-react'
import clsx from 'clsx'
import { CustomerDetailPaneBaseProps } from './customerDetailPane'
import { serverDateToLocaleDateString } from 'utils'
import { CustomerDetailsViewModel, CustomerInterfaceModel } from '../../api'
import { EditModalActionBar } from 'components'

interface ActivityLogProps extends CustomerDetailPaneBaseProps {
  className?: string
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    key: {
      color: theme.palette.neutralButton.main,
    },
    customerActivity: {
      marginTop: 0,
      // counter the bottom margin on the grid rows
      marginBottom: -1 * parseInt(theme.spacing(4)),
    },
    connectorActivity: {
      // counter the margin on the grid rows and connector names
      marginBottom: -1 * parseInt(theme.spacing(8)),
      marginTop: -1 * parseInt(theme.spacing(4)),
    },
    accordionContent: {
      width: '100%',
    },
    loader: {},
    connectorName: {
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
    },
    item: {
      marginBottom: theme.spacing(4),
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    scrollable: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
  }),
)

type GroupedConnectorHistory = {
  [ key: string]: CustomerInterfaceModel[]
}

const groupAndSortConnectorsByActivity = (connectors: CustomerInterfaceModel[]) => {
  // we want newest activities first
  const groupedConnectors: GroupedConnectorHistory = connectors.reduce((prev: GroupedConnectorHistory, current: CustomerInterfaceModel) => {
    if (!current.customerInterfaceId) return prev

    const connectorHistory: CustomerInterfaceModel[] = prev[current.customerInterfaceId] ?? []

    return {
      ...prev,
      [current.customerInterfaceId]: [...connectorHistory, current].sort((a, b) =>
        a.createdOn > b.createdOn ? -1 : b.createdOn > a.createdOn ? 1 : 0,
      ),
    }
  }, {} as GroupedConnectorHistory)

  // return an array sorted on created date.  right now only return the first item in each array.
  // later we can figure out what additional details to show
  return Object.keys(groupedConnectors).sort((a, b) =>
    groupedConnectors[a][0].createdOn > groupedConnectors[b][0].createdOn ? -1 : groupedConnectors[b][0].createdOn > groupedConnectors[a][0].createdOn ? 1 : 0,
  ).map(id => groupedConnectors[id][0])
}

const getSupportUserDisplayLabel = (
  id: string | null | undefined,
  username: string | null | undefined,
) => {
  if (!username) return id

  return username
}

export const ActivityLog: React.FunctionComponent<ActivityLogProps> = ({
  customerAgencyId,
  onClose,
  className,
}) => {
  const classes = useStyles({})
  const logger = useLogger()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const { user: authUser } = useAuth()

  const { customersContext } = useCustomers()
  const { getCustomerDetails } = useCustomerApi()

  const [customerDetails, setCustomerDetails] = React.useState<
    AsyncPayload<CustomerDetailsViewModel>
  >({
    payload: null,
    isLoading: true,
  })

  React.useEffect(() => {
    const loadData = async () => {
      const response = await getCustomerDetails(customerAgencyId)
      if (response.status === 200) {
        const details = response.data
        details.connectors = groupAndSortConnectorsByActivity(details.connectors)
        setCustomerDetails({
          payload: details,
          isLoading: false,
        })
      } else {
        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error loading the customer details',
        })
      }
    }

    loadData()
  }, [customerAgencyId])

  // if the customer doesn't exist in the context then return
  const customer = customersContext.customers[customerAgencyId]
  if (!customer) {
    return null
  }

  const typedCustomerDetails = customerDetails.payload!

  return (
    <Box className={clsx(classes.root, className)}>
      <Box className={classes.scrollable}>
        <Accordion
          id="Customer::ActivityLog::Customer"
          title="Customer"
          initiallyExpanded
        >
          <Box
            className={clsx(classes.accordionContent, classes.customerActivity)}
          >
            <Grid container>
              <Grid item xs={4} className={classes.item}>
                <Typography
                  variant="body2"
                  display="inline"
                  className={classes.key}
                >
                  Created
                </Typography>
              </Grid>
              <Grid item xs={8} className={classes.item}>
                <Typography variant="body2" display="inline">
                  {serverDateToLocaleDateString(customer.supportCreatedOn)}
                </Typography>
              </Grid>
              <Grid item xs={4} className={classes.item}>
                <Typography
                  variant="body2"
                  display="inline"
                  className={classes.key}
                >
                  Created By
                </Typography>
              </Grid>
              <Grid item xs={8} className={classes.item}>
                <Typography variant="body2" display="inline">
                  {getSupportUserDisplayLabel(
                    customer.createdBySupportUserId,
                    customer.createdBySupportUserName,
                  )}
                </Typography>
              </Grid>
              <Grid item xs={4} className={classes.item}>
                <Typography
                  variant="body2"
                  display="inline"
                  className={classes.key}
                >
                  Updated
                </Typography>
              </Grid>
              <Grid item xs={8} className={classes.item}>
                <Typography variant="body2" display="inline">
                  {serverDateToLocaleDateString(customer.supportUpdatedOn)}
                </Typography>
              </Grid>
              <Grid item xs={4} className={classes.item}>
                <Typography
                  variant="body2"
                  display="inline"
                  className={classes.key}
                >
                  Updated By
                </Typography>
              </Grid>
              <Grid item xs={8} className={classes.item}>
                <Typography variant="body2" display="inline">
                  {getSupportUserDisplayLabel(
                    customer.updatedBySupportUserId,
                    customer.updatedBySupportUserName,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Accordion>
        <Accordion
          id="Customer::ActivityLog::Connectors"
          title="Connectors"
          initiallyExpanded
        >
          <Box
            className={clsx(
              classes.accordionContent,
              classes.connectorActivity,
            )}
          >
            {customerDetails.isLoading && (
              <Loader
                size="sm"
                text="Loading Customer Details"
                className={classes.loader}
              />
            )}
            {!customerDetails.isLoading &&
              customerDetails.payload !== null &&
              typedCustomerDetails.connectors.map((connector) => (
                <Box
                  key={`Customer::ActivityLog::Connectors::${connector.customerInterfaceId}`}
                >
                  <Typography variant="h4" className={classes.connectorName}>
                    {connector.name}
                  </Typography>
                  <Grid container>
                    <Grid item xs={4} className={classes.item}>
                      <Typography
                        variant="body2"
                        display="inline"
                        className={classes.key}
                      >
                        Created
                      </Typography>
                    </Grid>
                    <Grid item xs={8} className={classes.item}>
                      <Typography variant="body2" display="inline">
                        {serverDateToLocaleDateString(connector.createdOn)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
          </Box>
        </Accordion>
        <Box className={classes.spacer} />
      </Box>
      <EditModalActionBar
        saveButtonType="button"
        canDelete={false}
        saveText="Close"
        saveVariant="outlined"
        saveColor="primary"
        canCancel={false}
        onSave={onClose}
      />
    </Box>
  )
}
